import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Chip,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HistoryIcon from '@mui/icons-material/History';
import {
  GRADE_DEFINITIONS,
  GRADE_COLORS,
  ASSESSMENT_META,
  RISK_CATEGORIES
} from './shaReportData';

const DARK_GREEN = '#1D4434';

/**
 * Render a small coloured badge for a grade letter (A/B/C/D/F).
 * Sizes: 'sm' for inline/legend/table use, 'lg' for the header hero.
 */
function GradeBadge({ grade, size = 'sm' }) {
  const colour = GRADE_COLORS[grade] || GRADE_COLORS.C;
  const dim = size === 'lg' ? 72 : 32;
  return (
    <Avatar
      sx={{
        width: dim,
        height: dim,
        bgcolor: colour.bg,
        color: colour.fg,
        fontWeight: 700,
        fontSize: size === 'lg' ? 36 : 16,
        border: `2px solid ${colour.main}`,
        boxShadow: size === 'lg' ? `0 0 0 4px ${colour.main}25` : 'none'
      }}
    >
      {grade}
    </Avatar>
  );
}

/**
 * SHA > Report — Executive-style SHA 2.0 assessment read-out.
 * Source content: SHA 2.0 deck (slides 4, 5, 6). Data lives in shaReportData.js.
 */
const SHAReport = () => {
  const [filterGrade, setFilterGrade] = useState('all');
  const [expandedKey, setExpandedKey] = useState(null);

  // Build the grade distribution so we can show "how many of each grade" without
  // another data source. This mirrors the at-a-glance bar in the PPT summary.
  const gradeDistribution = useMemo(() => {
    const counts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    for (const c of RISK_CATEGORIES) {
      if (counts[c.grade] != null) counts[c.grade] += 1;
    }
    return counts;
  }, []);

  const totalFindings = useMemo(
    () => RISK_CATEGORIES.reduce((sum, c) => sum + (c.findings?.total || 0), 0),
    []
  );
  const totalCriticalHigh = useMemo(
    () => RISK_CATEGORIES.reduce((sum, c) => sum + (c.findings?.criticalHigh || 0), 0),
    []
  );

  const visibleCategories = useMemo(() => {
    if (filterGrade === 'all') return RISK_CATEGORIES;
    return RISK_CATEGORIES.filter((c) => c.grade === filterGrade);
  }, [filterGrade]);

  const overallColour = GRADE_COLORS[ASSESSMENT_META.overallGrade] || GRADE_COLORS.C;

  return (
    <Box>
      {/* ====== HEADER / HERO ====== */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${DARK_GREEN}f2 0%, ${DARK_GREEN}d9 100%)`,
          color: 'white'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="overline" sx={{ letterSpacing: '0.12em', opacity: 0.85 }}>
              Service Health Assessment — Report
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
              {ASSESSMENT_META.product} · SHA 2.0
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap">
              <Chip
                icon={<CalendarTodayIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                label={`Assessment Date: ${ASSESSMENT_META.assessmentDate}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<HistoryIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                label={`Prior: ${ASSESSMENT_META.priorAssessment.date} (${ASSESSMENT_META.priorAssessment.grade})`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Tooltip title="Prior-assessment high/critical recommendations still open">
                <Chip
                  icon={<ReportProblemIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                  label={`${ASSESSMENT_META.priorAssessment.highCriticalOutstanding} outstanding high/critical`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Tooltip>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={4}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 2.5
              }}
            >
              <GradeBadge grade={ASSESSMENT_META.overallGrade} size="lg" />
              <Box flex={1}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                  Overall SHA Grade
                </Typography>
                <Typography variant="h5" sx={{ color: overallColour.fg, fontWeight: 700, lineHeight: 1.1 }}>
                  {GRADE_DEFINITIONS.find((g) => g.grade === ASSESSMENT_META.overallGrade)?.summary || ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>{totalFindings}</strong> findings across {RISK_CATEGORIES.length} risk categories
                  {totalCriticalHigh > 0 && (
                    <>
                      {' '}· <strong style={{ color: GRADE_COLORS.F.fg }}>{totalCriticalHigh} critical / high</strong>
                    </>
                  )}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* ====== GRADE LEGEND ====== */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <AssessmentIcon sx={{ color: DARK_GREEN, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: DARK_GREEN }}>
            High-Level SHA Grade Definitions
          </Typography>
        </Stack>
        <Grid container spacing={1.5}>
          {GRADE_DEFINITIONS.map(({ grade, summary, description }) => {
            const c = GRADE_COLORS[grade];
            return (
              <Grid item xs={12} sm={6} md={2.4} key={grade}>
                <Box
                  sx={{
                    p: 1.5,
                    height: '100%',
                    borderRadius: 1.5,
                    border: `1px solid ${c.main}40`,
                    bgcolor: c.bg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <GradeBadge grade={grade} />
                    <Typography variant="subtitle2" sx={{ color: c.fg, fontWeight: 700 }}>
                      {summary}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* ====== GRADE DISTRIBUTION ====== */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: DARK_GREEN, mb: 1.5 }}>
          Grade Distribution Across Risk Categories
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
          {Object.entries(gradeDistribution).map(([grade, count]) => {
            const c = GRADE_COLORS[grade];
            const pct = RISK_CATEGORIES.length
              ? Math.round((count / RISK_CATEGORIES.length) * 100)
              : 0;
            return (
              <Box
                key={grade}
                sx={{
                  flex: 1,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: count > 0 ? `${c.main}60` : 'divider',
                  bgcolor: count > 0 ? c.bg : 'transparent',
                  opacity: count === 0 ? 0.55 : 1
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <GradeBadge grade={grade} />
                  <Box flex={1}>
                    <Typography variant="h5" sx={{ color: c.fg, fontWeight: 700, lineHeight: 1 }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {count === 1 ? 'category' : 'categories'}
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    mt: 1,
                    height: 6,
                    borderRadius: 1,
                    bgcolor: `${c.main}15`,
                    '& .MuiLinearProgress-bar': { bgcolor: c.main }
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {/* ====== RISK CATEGORY DASHBOARD ====== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: DARK_GREEN }}>
            SHA Risk Category Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click any category to see the full justification for its grade.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filterGrade}
          onChange={(_e, v) => v && setFilterGrade(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500 } }}
        >
          <ToggleButton value="all">All ({RISK_CATEGORIES.length})</ToggleButton>
          {Object.entries(gradeDistribution)
            .filter(([, count]) => count > 0)
            .map(([grade, count]) => (
              <ToggleButton key={grade} value={grade}>
                <span
                  style={{
                    color: GRADE_COLORS[grade].fg,
                    fontWeight: 700,
                    marginRight: 4
                  }}
                >
                  {grade}
                </span>
                ({count})
              </ToggleButton>
            ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>
        {visibleCategories.map((cat) => {
          const c = GRADE_COLORS[cat.grade] || GRADE_COLORS.C;
          const isExpanded = expandedKey === cat.key;
          const hasCriticalHigh = (cat.findings?.criticalHigh || 0) > 0;
          return (
            <Grid item xs={12} md={6} key={cat.key}>
              <Accordion
                expanded={isExpanded}
                onChange={(_e, exp) => setExpandedKey(exp ? cat.key : null)}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: isExpanded ? c.main : 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                  '&:before': { display: 'none' },
                  '&:hover': { borderColor: c.main, boxShadow: `0 2px 6px ${c.main}20` }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: isExpanded ? c.bg : 'background.paper',
                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2, my: 0 }
                  }}
                >
                  <GradeBadge grade={cat.grade} />
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
                    >
                      {cat.title}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={`${cat.findings.total} finding${cat.findings.total === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ height: 22, fontWeight: 500 }}
                      />
                      <Chip
                        label={`${cat.findings.criticalHigh} critical / high`}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 600,
                          color: hasCriticalHigh ? GRADE_COLORS.F.fg : 'text.secondary',
                          bgcolor: hasCriticalHigh ? GRADE_COLORS.F.bg : 'action.hover',
                          border: hasCriticalHigh ? `1px solid ${GRADE_COLORS.F.main}40` : 'none'
                        }}
                      />
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0, pb: 2, bgcolor: 'background.paper' }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', letterSpacing: '0.08em', mb: 1 }}
                  >
                    Justification for Grade
                  </Typography>
                  <Stack spacing={1.25}>
                    {cat.justification.map((para, idx) => (
                      <Typography key={idx} variant="body2" sx={{ lineHeight: 1.65 }}>
                        {para}
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </Grid>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3, textAlign: 'right', fontStyle: 'italic' }}
      >
        Source: SHA 2.0 assessment deck · {ASSESSMENT_META.assessmentDate}
      </Typography>
    </Box>
  );
};

export default SHAReport;
